import { useEffect, useState } from "react";
import {
  searchPlaces,
  type PlacePrediction,
} from "@src/packages/clinics/utils/googlePlaces";

type Coordinates = { latitude: number; longitude: number };

type UsePlacesPredictionsOptions = {
  /** The current (raw) search text. Trimmed internally. */
  query: string;
  /** Minimum trimmed length before searching. Default 2. */
  minChars?: number;
  /**
   * Debounce before hitting the Places API, in ms. Default 250. Pass 0 when the
   * caller already debounces the query (for example a search control that
   * debounces its own input) to avoid debouncing twice.
   */
  debounceMs?: number;
  /** Restrict to Google's `(cities)` collection. */
  citiesOnly?: boolean;
  /** Restrict to Google's `(regions)` collection (cities, states, countries). */
  regionsOnly?: boolean;
  /**
   * Bias results toward these coordinates without hiding far-away ones. Omit to
   * fall back to Google's default per-request IP biasing.
   */
  biasLocation?: Coordinates | null;
  /** When false, the hook stays idle and reports no predictions. Default true. */
  enabled?: boolean;
};

/**
 * Shared plumbing for a Places autocomplete input: turns a search query into
 * ranked predictions, owning the debounce, the min-length gate, and the
 * stale-request guard that every location input otherwise re-implements (and
 * occasionally gets wrong). Selection/extraction stays with the caller, since
 * each surface pulls different fields out of the resolved place.
 */
export function usePlacesPredictions({
  query,
  minChars = 2,
  debounceMs = 250,
  citiesOnly,
  regionsOnly,
  biasLocation,
  enabled = true,
}: UsePlacesPredictionsOptions): {
  predictions: PlacePrediction[];
  isSearching: boolean;
} {
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  // Depend on the coordinate primitives, not the object identity, so a
  // re-rendered bias object does not re-trigger the search.
  const biasLatitude = biasLocation?.latitude;
  const biasLongitude = biasLocation?.longitude;

  useEffect(() => {
    if (!enabled) {
      setPredictions([]);
      setIsSearching(false);
      return;
    }

    const trimmed = query.trim();
    if (trimmed.length < minChars) {
      setPredictions([]);
      setIsSearching(false);
      return;
    }

    // Report "searching" as soon as a query is pending, including during the
    // debounce window, so the caller can show the state without its own timer.
    setIsSearching(true);
    // Each effect run owns `active`; its cleanup flips it off before the next
    // run, so a slow in-flight response for a stale query is ignored.
    let active = true;
    const bias =
      biasLatitude != null && biasLongitude != null
        ? { latitude: biasLatitude, longitude: biasLongitude }
        : null;

    const run = () => {
      searchPlaces(trimmed, {
        ...(citiesOnly ? { citiesOnly: true } : {}),
        ...(regionsOnly ? { regionsOnly: true } : {}),
        ...(bias ? { locationBias: bias, origin: bias } : {}),
      })
        .then((results) => {
          if (!active) return;
          setPredictions(results);
          setIsSearching(false);
        })
        .catch(() => {
          if (!active) return;
          setPredictions([]);
          setIsSearching(false);
        });
    };

    if (debounceMs <= 0) {
      run();
      return () => {
        active = false;
      };
    }

    const handle = setTimeout(run, debounceMs);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [
    query,
    minChars,
    debounceMs,
    citiesOnly,
    regionsOnly,
    biasLatitude,
    biasLongitude,
    enabled,
  ]);

  return { predictions, isSearching };
}
